import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  viewChild
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { isEqual as _isEqual } from 'lodash';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import {
  CreateCustomPlaceCommand,
  CustomResult,
  CustomResultOfZoneDto,
  GeometryType,
  Polygon,
  SmsAggregator,
  UpdateCustomPlaceCommand,
} from 'src/app/core/api/models';
import { CustomPlaceService, ZonesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { ConsoleService } from 'src/app/core/services/console.service';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { ZonesSelectSearchComponent } from 'src/app/shared/components/select-search/zones-select-search.component';
import { SmartSendMapControlComponent } from 'src/app/shared/components/smart-send-map-control/smart-send-map-control.component';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { FormPageConfig } from './../../../../core/models/form-page-config.model';
import { AppStateService } from './../../../../core/services/app-state.service';

@Component({
    selector: 'app-custom-place-form',
    templateUrl: './custom-place-form.component.html',
    styleUrls: ['./custom-place-form.component.scss'],
    standalone: false
})
export class CustomPlaceFormComponent
  extends BaseFormPageComponent
  implements OnInit
{
  appStateService = inject(AppStateService);
  //#region Properties
  @Input('showAddToPublicPlace') showAddToPublicPlace: boolean;
  @Input() customPlaceFormDialog: boolean = false;
  @Input() isShowManualPolygon: boolean = true;
  @Input('polygon') selectedSmartPolygon: any;
  @Input('polygonTitles') polygonTitles: any;

  operators: Array<SmsAggregator> = [];
  polygon: any = null;
  model: CreateCustomPlaceCommand = {};
  formPageConfig: FormPageConfig<any> = {};

  get GeometryType() {
    return GeometryType;
  }

  //zones
  selectedState;

  stateId: number = 0;
  cityId: number = -1;

  readonly citySelectSearch = viewChild<ZonesSelectSearchComponent>('citySelectSearch');

  readonly Map = viewChild<SmartSendMapControlComponent>('Map');

  CustomPlaceId: number | null = null;
  selectedPlacePolygon: any;

  isShowMap: boolean = true;

  @Output('close') close = new EventEmitter<boolean>();

  //#endregion

  constructor(
    protected currentService: CustomPlaceService,
    private zoneService: ZonesService,
    public dialogService: DialogService,
    public translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  //#region init methods

  initForm() {
    this.form = new FormGroup({
      coordinates: new FormControl(''),
      geometryType: new FormControl(''),
      isActive: new FormControl(''),
      city: new FormControl('', Validators.required),
      title: new FormControl('', Validators.required),
      province: new FormControl('', Validators.required),
    });
  }

  initModel() {
    try {
      // when form is edit mode
      if (this.formPageConfig.isEditMode) {
        this.model = Object.assign({}, this.formPageConfig.editData);
      } else {
        this.selectedState = null;

        setTimeout(() => {
          this.isShowMap = false;
        }, 10);
      }
      setTimeout(() => {
        this.isShowMap = true;
      }, 600);

      if (this.model.cityId) this.getSelectedState(this.model.cityId);
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smartSms);
    }
  }

  onMapReady() {
    this.getCoordinate();
  }

  //#endregion

  //#region Api

  //Create
  baseCreateApi(model: CreateCustomPlaceCommand) {
    this.unSubs();
    this.loading = true;
    this.currentService
      .customPlaceCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });
          if (result.isSucceed) {
            this.formPageConfig.isEditMode = false;
            this.onSuccess();
            if (this.showAddToPublicPlace) this.emitClose();
          }
        },
        (error) => {
          ConsoleService.error('error', error);
        },
        () => (this.loading = false)
      );
  }

  //Update
  baseUpdateApi(model: UpdateCustomPlaceCommand) {
    this.unSubs();

    this.loading = true;
    this.currentService
      .customPlaceUpdatePut({ id: model.id.toString(), body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  getSelectedState(cityId) {
    this.unSubs();

    //get city by zoneId
    this.getZoneById(cityId)
      .pipe(
        map((city: CustomResultOfZoneDto) => {
          this.selectedState = city?.data?.parentId;
          return city;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  // get zone by id
  getZoneById(zoneId: number): Observable<CustomResultOfZoneDto> {
    return this.zoneService.zonesGetZoneById({ Id: zoneId });
  }

  onChangeZones(zoneId) {
    this.model.cityId = -1;
    this.selectedState = zoneId;
    this.model.provinceId = zoneId;
  }

  onCityChange($event) {
    this.cityId = $event;
    this.zoneService
      .zonesGetZoneCoordinates({ ZoneId: this.cityId })
      .subscribe((result) => {
        try {
          let center = JSON.parse(result?.data) as mapboxgl.LngLatLike;
          var tmp = center[0];
          center[0] = center[1];
          center[1] = tmp;
          this.Map().SetCenter(center);
        } catch (error) {
          this.loggerService.logError(this.tagName, error, EventTypes.smartSms);
        }
      });
  }

  ConvertPolygonsTo3plNumberArray(input: any): number[][][] | null {
    var pls: number[][][] = [];
    input.forEach((polygon) => {
      var item: number[][] = [];
      polygon[0].forEach((it2: any) => {
        item.push(it2);
      });
      pls.push(item);
    });
    return pls;
  }

  onSubmit() {
    if (!this.isValidForm(this.form, this.tagName)) return;
    if (this.loading) return;
    let drawedPolygons: Array<Polygon> = [];

    if (!this.customPlaceFormDialog) {
      let selectedPolygons;
      let polygonsTitle: any[] = [];
      const Map = this.Map();
      if (Map) {
        selectedPolygons = this.ConvertPolygonsTo3plNumberArray(
          Map.getAllPolygons()
        );
        polygonsTitle = Map.getAllPolygonTitles();
      }

      if (selectedPolygons?.length > 0) {
        selectedPolygons.forEach((element) => {
          let polygonObject = polygonsTitle.find((item) =>
            _isEqual(item.coordinates, element)
          );

          let polygon: Polygon = {
            coordinates: element,
            title: polygonObject?.title,
          };
          drawedPolygons.push(polygon);
        });
      } else if (this.polygon) {
        let polygon: Polygon = {
          coordinates: JSON.parse(this.polygon.toString()),
        };
        drawedPolygons.push(polygon);
      } else {
        drawedPolygons = [];
      }
      this.model.polygons = drawedPolygons;
    }

    if (this.showAddToPublicPlace) {
      if (!this.model.polygons) this.model.polygons = [{}];

      if ([this.selectedSmartPolygon]?.length > 0) {
        [this.selectedSmartPolygon].forEach((element) => {
          let polygonObject = this.polygonTitles.find((item) =>
            _isEqual(item.coordinates, element)
          );

          let polygon: Polygon = {
            coordinates: element,
            title: polygonObject?.title,
          };
          drawedPolygons.push(polygon);
        });
      }
      this.model.polygons = drawedPolygons;
    } else this.Map().onSaveButtonClick(null);

    this.checkCreateOrUpdate();
  }

  getCoordinate() {
    try {
      let item = this.model;
      this.selectedPlacePolygon = item?.polygons[0].coordinates;
      if (item?.polygons[0]?.coordinates) {
        for (let i = 0; i < item?.polygons.length; i++) {
          this.Map().onAddShowPolygonAutomatically(
            item?.polygons[i]?.coordinates
          );
          ConsoleService.info('Custom place is :', item);
        }
      }
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.smartSms);
    }
  }

  showMapHelp(event) {
    this.dialogService.openDialog({
      title: this.translate.instant('common.help'),
      message: 'messages.mapHelp',
      status: DialogStatus.info,
    });
  }

  onReset() {
    super.onReset();
    if (this.formPageConfig.isEditMode) this.onMapReady();
    else {
      this.selectedPlacePolygon = '';
      this.Map().removePolygon();
    }
  }

  emitClose() {
    this.close.emit(true);
  }
}
